import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { PresaveCampaignStatus } from 'fym-shared/src/smartlinks/presave';
import { TypedMemo } from '../../common/types';
import { formatShortDay } from './presaveDates';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveStatusPillProps {
  status: PresaveCampaignStatus;
  /** The release day (`YYYY-MM-DD`). A scheduled campaign says until when fans can pre-save. */
  releaseDate?: string;
}

/**
 * Where the campaign stands, in the panel header. Scheduled reads violet
 * because it is the live state, released green, and the two dead states grey —
 * a cancelled campaign should not compete with the numbers under it.
 *
 * The ids are spelled out: the extractor needs them static.
 */
const PresaveStatusPillComponent: React.FC<PresaveStatusPillProps> = ({ status, releaseDate }) => {
  const intl = useIntl();

  switch (status) {
    case PresaveCampaignStatus.released:
      return (
        <span className={clsx(styles.pill, styles.pillReleased)}>
          <i className={styles.dot} />
          {intl.formatMessage({ id: 'app.sml.presave.status.released', defaultMessage: 'Released' })}
        </span>
      );
    case PresaveCampaignStatus.expired:
      return (
        <span className={styles.pill}>
          <i className={styles.dot} />
          {intl.formatMessage({ id: 'app.sml.presave.status.expired', defaultMessage: 'Expired' })}
        </span>
      );
    case PresaveCampaignStatus.cancelled:
      return (
        <span className={styles.pill}>
          <i className={styles.dot} />
          {intl.formatMessage({ id: 'app.sml.presave.status.cancelled', defaultMessage: 'Cancelled' })}
        </span>
      );
    case PresaveCampaignStatus.scheduled:
    default:
      return (
        <span className={clsx(styles.pill, styles.pillScheduled)}>
          <i className={styles.dot} />
          {releaseDate === undefined
            ? intl.formatMessage({ id: 'app.sml.presave.status.scheduled', defaultMessage: 'Scheduled' })
            : intl.formatMessage(
                // One state word next to "Live" (QA finding lit-12): not "Scheduled".
                { id: 'app.sml.presave.status.open_until', defaultMessage: 'Pre-save open until {date}' },
                { date: formatShortDay(intl, releaseDate) }
              )}
        </span>
      );
  }
};

export const PresaveStatusPill = TypedMemo(PresaveStatusPillComponent);
